import {Record, List, Map} from 'immutable';
import {Store} from '@store/@types/alt';
import immutableStore from 'utils/immutableStore';
import alt from 'components/Dispatcher';
import CoachingPlanActions from 'actions/coaching/CoachingPlanActions';
import CoachingReviewActions from 'actions/coaching/CoachingReviewActions';
import {associateBy} from 'utils/associateBy';
import CoachingPlan, {PlanId} from 'records/CoachingPlan';
import {UserId} from 'records/User';

class CoachingPlanStoreState extends Record({
  coachingPlans: List(), // List<CoachingPlan>
  coachingPlansForManager: List(), // List<CoachingPlan>
  planMap: Map(), // Map<PlanId, CoachingPlan>
  areCoachingPlansLoading: false,
  areCoachingPlansForManagerLoading: false,
  wasErrorLoadingCoachingPlans: false,
  wasErrorLoadingCoachingPlansForManager: false,

  lastCompletePlan: Map(), // Map<UserId, CoachingPlan>
  lastCompletePlanLoading: false,
  lastCompletePlanError: false,

  overduePlans: Map(), // Map<UserId, List<CoachingPlan>
  overduePlansLoading: false,
  overduePlansError: false,

  retrieveLoading: false,
  retrieveError: false,
}) {
  readonly coachingPlans: List<CoachingPlan>;
  readonly coachingPlansForManager: List<CoachingPlan>;
  readonly planMap: Map<PlanId, CoachingPlan>;
  readonly areCoachingPlansLoading: boolean;
  readonly areCoachingPlansForManagerLoading: boolean;
  readonly wasErrorLoadingCoachingPlans: boolean;
  readonly wasErrorLoadingCoachingPlansForManager: boolean;

  readonly lastCompletePlan: Map<UserId, CoachingPlan>;
  readonly lastCompletePlanLoading: boolean;
  readonly lastCompletePlanError: boolean;

  readonly overduePlans: Map<UserId, List<CoachingPlan>>;
  readonly overduePlansLoading: boolean;
  readonly overduePlansError: boolean;

  readonly retrieveLoading: boolean;
  readonly retrieveError: boolean;
}

@immutableStore
class CoachingPlanStore extends Store<CoachingPlanStoreState> {
  constructor() {
    super();
    this.state = new CoachingPlanStoreState();
    this.bindActions(CoachingPlanActions);
    this.bindActions(CoachingReviewActions);
  }

  fetchAll() {
    this.setState({
      areCoachingPlansLoading: true,
      wasErrorLoadingCoachingPlans: false,
    });
  }

  fetchForCoaches() {
    this.setState({
      areCoachingPlansLoading: true,
      wasErrorLoadingCoachingPlans: false,
    });
  }

  fetchAllSuccess(coachingPlans) {
    this.setState({
      coachingPlans,
      planMap: associateBy(coachingPlans, p => p.id, this.state.planMap),
      areCoachingPlansLoading: false,
    });
  }

  fetchAllError() {
    this.setState({
      areCoachingPlansLoading: false,
      wasErrorLoadingCoachingPlans: true,
    });
  }

  fetchForManagerWithReviewsInRangeSuccess(coachingPlansForManager) {
    // this one does not store per coach, consider using DirectReportsStore.fetchPlansForCoach instead
    this.setState({
      coachingPlansForManager,
      areCoachingPlansForManagerLoading: false,
    });
  }

  fetchForManagerWithReviewsInRangeError() {
    this.setState({
      areCoachingPlansForManagerLoading: false,
      wasErrorLoadingCoachingPlansForManager: true,
    });
  }

  fetchLastCompleteAndOverdue() {
    this.setState({
      lastCompletePlanLoading: true,
      lastCompletePlanError: false,
      overduePlansLoading: true,
      overduePlansError: false,
    });
  }

  fetchLastCompleteAndOverdueSuccess(planMap) {
    this.setState({
      lastCompletePlan: planMap.mapEntries(([k, v]) => [k, v.lastCompleted]),
      lastCompletePlanLoading: false,
      lastCompletePlanError: false,
      overduePlans: planMap.mapEntries(([k, v]) => [k, v.overdue]),
      overduePlansLoading: false,
      overduePlansError: false,
    });
  }

  fetchLastCompleteAndOverdueError(error) {
    console.error(error);
    this.setState({
      lastCompletePlanLoading: false,
      lastCompletePlanError: true,
      overduePlansLoading: false,
      overduePlansError: true,
    });
  }

  retrieve() {
    this.setState({
      retrieveLoading: true,
      retrieveError: false,
    });
  }

  retrieveSuccess(plan) {
    const {coachingPlans} = this.state;
    const coachingPlanIndex = coachingPlans.findIndex(cp => cp.id === plan.id);
    const replaced =
      coachingPlanIndex < 0 ? coachingPlans.push(plan) : coachingPlans.set(coachingPlanIndex, plan);
    this.setState({
      planMap: this.state.planMap.set(plan.id, plan),
      coachingPlans: replaced,
      retrieveLoading: false,
      retrieveError: false,
    });
  }

  retrieveError(e) {
    console.error(e);
    this.setState({
      retrieveLoading: false,
      retrieveError: true,
    });
  }

  createSuccess(plan) {}

  updateReviewSuccess(review) {
    // we may not have loaded the plan yet (maybe it was new?) - no need to updated planMap if so.
    if (!this.state.planMap.get(review.planId)) {
      return;
    }

    const reviews = this.state.planMap.getIn([review.planId, 'reviews'], List());
    const index = reviews.findIndex(r => r.id === review.id);
    const updatedReviews = reviews.set(index, review);

    this.setState({
      planMap: this.state.planMap.setIn([review.planId, 'reviews'], updatedReviews),
    });
  }

  updateReviewError(error) {
    console.error(error);
  }
}

export default alt.createStore(CoachingPlanStore);
